import {
  startOfWeek,
  addWeeks,
  eachDayOfInterval,
  subWeeks,
  addDays,
} from "date-fns";

const WEEKDAYS = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
} as const;

type WeekDay = keyof typeof WEEKDAYS;

export function getCalendarDays(
  currentDate: Date,
  weekStartsOn: WeekDay = "Monday",
  visibleWeeks = 5,
) {
  const startDay = WEEKDAYS[weekStartsOn];

  // Find the start of the week for the current date
  const weekStart = startOfWeek(currentDate, { weekStartsOn: startDay });

  const weeksBeforeStartDate = Math.floor((visibleWeeks - 1) / 2);
  const weeksAfterStartDate = visibleWeeks - weeksBeforeStartDate - 1;

  // Calculate the start date (x? weeks before) and end date (x? weeks after) 
  const calendarStart = subWeeks(weekStart, weeksBeforeStartDate);
  const calendarEnd = addDays(addWeeks(weekStart, weeksAfterStartDate), 6);

  // Get all days in the interval
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  return {
    days,
    emptyDays: 0, // No empty days needed since we're showing continuous weeks
    weekDays: getWeekDays(weekStartsOn),
  };
}

function getWeekDays(weekStartsOn: WeekDay): string[] {
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const startIndex = WEEKDAYS[weekStartsOn];
  return [...weekDays.slice(startIndex), ...weekDays.slice(0, startIndex)];
}

// Format date with localization support
export function formatDateWithLocale(date: Date, formatString: string, locale: string): string {
  const monthIndex = date.getMonth();
  const dayIndex = date.getDay();
  const day = date.getDate();
  const year = date.getFullYear();
  
  // Handle quoted strings by temporarily replacing them with placeholders
  const quotedStrings: string[] = [];
  let workingFormat = formatString.replace(/'([^']*)'/g, (_match, content) => {
    quotedStrings.push(content);
    return `__QUOTED_${quotedStrings.length - 1}__`;
  });
  
  const months = {
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    fr: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
    nl: ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december']
  };
  
  const monthsShort = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    fr: ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'],
    nl: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']
  };
  
  const days = {
    en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    fr: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
    nl: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag']
  };
  
  const daysShort = {
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    fr: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
    nl: ['zon', 'maa', 'din', 'woe', 'don', 'vri', 'zat']
  };
  
  // Handle complex format strings by replacing tokens
  let result = workingFormat;
  
  // Replace month names (longest patterns first)
  result = result.replace(/MMMM/g, (months as any)[locale]?.[monthIndex] || months.en[monthIndex]);
  result = result.replace(/MMM/g, (monthsShort as any)[locale]?.[monthIndex] || monthsShort.en[monthIndex]);
  result = result.replace(/MM/g, String(monthIndex + 1).padStart(2, '0'));
  // Replace M only when NOT part of MM, MMM, or MMMM (not preceded or followed by M)
  // and NOT followed by a letter (including accented characters)
  // Using Unicode letter class \p{L} to match all letters including accented ones
  result = result.replace(/(?<!M)M(?!M)(?![\p{L}])/gu, String(monthIndex + 1));
  
  // Replace day names
  result = result.replace(/EEEE/g, (days as any)[locale]?.[dayIndex] || days.en[dayIndex]);
  result = result.replace(/EEE/g, (daysShort as any)[locale]?.[dayIndex] || daysShort.en[dayIndex]);
  
  // Replace day numbers
  result = result.replace(/dd/g, String(day).padStart(2, '0'));
  // Replace d only when NOT part of dd (not preceded or followed by d)
  // and NOT followed by a letter (including accented characters)
  // Using Unicode letter class \p{L} to match all letters including accented ones
  result = result.replace(/(?<!d)d(?!d)(?![\p{L}])/gu, String(day));
  
  // Replace year
  result = result.replace(/yyyy/g, String(year));
  result = result.replace(/yy/g, String(year).slice(-2));
  
  // Replace hour/minute/second if present
  // Use negative lookahead/lookbehind to avoid matching letters in already-replaced month names
  // Using Unicode letter class \p{L} to match all letters including accented ones
  result = result.replace(/HH/g, String(date.getHours()).padStart(2, '0'));
  result = result.replace(/mm/g, String(date.getMinutes()).padStart(2, '0'));
  result = result.replace(/(?<![\p{L}])h(?![\p{L}])/gu, String(date.getHours() % 12 || 12));
  result = result.replace(/(?<![\p{L}])a(?![\p{L}])/gu, date.getHours() >= 12 ? 'PM' : 'AM');
  
  // Restore quoted strings
  result = result.replace(/__QUOTED_(\d+)__/g, (_match, index) => quotedStrings[parseInt(index)]);
  
  return result;
}